/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetFrontTipiDomandaIdIntermediazioniBeneficiariParams,
  GetFrontTipiDomandaParams,
  IntermediazioneDTO,
  ModuloSezioneCompletoDTO,
  TipoDomandaDTO,
  TipoDomandaModuloDTO,
  TipoDomandaRequest,
  WsProblema,
  WsRispostaPaginataListTipoDomandaDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Ottiene i dettagli dell'ultima versione pubblicata del tipo domanda in base al idGruppo
 * @summary Recupera l'ultima versione pubblicata del tipo domanda in base al idGruppo
 */
export const getFrontTipiDomandaId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoDomandaDTO>(
    { url: `/framework-cmp-domande/tipi-domanda/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetFrontTipiDomandaIdQueryKey = (id: string) =>
  [`/framework-cmp-domande/tipi-domanda/${id}`] as const;

export const getGetFrontTipiDomandaIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaId>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontTipiDomandaId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontTipiDomandaIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontTipiDomandaId>>
  > = ({ signal }) => getFrontTipiDomandaId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontTipiDomandaIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontTipiDomandaId>>
>;
export type GetFrontTipiDomandaIdQueryError = ErrorType<WsProblema>;

/**
 * @summary Recupera l'ultima versione pubblicata del tipo domanda in base al idGruppo
 */
export const useGetFrontTipiDomandaId = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaId>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontTipiDomandaIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna il tipo domanda
 * @summary Aggiorna tipo domanda
 */
export const putFrontTipiDomandaId = (
  id: string,
  tipoDomandaRequest: TipoDomandaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/tipi-domanda/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: tipoDomandaRequest,
    },
    options,
  );
};

export const getPutFrontTipiDomandaIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontTipiDomandaId>>,
    TError,
    { id: string; data: TipoDomandaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putFrontTipiDomandaId>>,
  TError,
  { id: string; data: TipoDomandaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putFrontTipiDomandaId>>,
    { id: string; data: TipoDomandaRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putFrontTipiDomandaId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutFrontTipiDomandaIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putFrontTipiDomandaId>>
>;
export type PutFrontTipiDomandaIdMutationBody = TipoDomandaRequest;
export type PutFrontTipiDomandaIdMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna tipo domanda
 */
export const usePutFrontTipiDomandaId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontTipiDomandaId>>,
    TError,
    { id: string; data: TipoDomandaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutFrontTipiDomandaIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce l'elenco dei tipi domanda per l'utente compilatore.
 * @summary Elenco tipo domande Frontoffice
 */
export const getFrontTipiDomanda = (
  params?: GetFrontTipiDomandaParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListTipoDomandaDTO>(
    {
      url: `/framework-cmp-domande/tipi-domanda`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontTipiDomandaQueryKey = (
  params?: GetFrontTipiDomandaParams,
) =>
  [`/framework-cmp-domande/tipi-domanda`, ...(params ? [params] : [])] as const;

export const getGetFrontTipiDomandaQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomanda>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontTipiDomandaParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomanda>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontTipiDomanda>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontTipiDomandaQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontTipiDomanda>>
  > = ({ signal }) => getFrontTipiDomanda(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontTipiDomandaQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontTipiDomanda>>
>;
export type GetFrontTipiDomandaQueryError = ErrorType<unknown>;

/**
 * @summary Elenco tipo domande Frontoffice
 */
export const useGetFrontTipiDomanda = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomanda>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontTipiDomandaParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomanda>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontTipiDomandaQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ritorna l'elenco di tutti i moduli associati al tipoDomanda.
 * @summary Elenco Moduli di un TipoDomanda
 */
export const getFrontTipiDomandaIdModuli = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoDomandaModuloDTO[]>(
    {
      url: `/framework-cmp-domande/tipi-domanda/${id}/moduli`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontTipiDomandaIdModuliQueryKey = (id: string) =>
  [`/framework-cmp-domande/tipi-domanda/${id}/moduli`] as const;

export const getGetFrontTipiDomandaIdModuliQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaIdModuli>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaIdModuli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontTipiDomandaIdModuli>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontTipiDomandaIdModuliQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontTipiDomandaIdModuli>>
  > = ({ signal }) => getFrontTipiDomandaIdModuli(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontTipiDomandaIdModuliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontTipiDomandaIdModuli>>
>;
export type GetFrontTipiDomandaIdModuliQueryError = ErrorType<unknown>;

/**
 * @summary Elenco Moduli di un TipoDomanda
 */
export const useGetFrontTipiDomandaIdModuli = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaIdModuli>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaIdModuli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontTipiDomandaIdModuliQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ritorna il dettaglio di un modulo di un tipo domanda PUBBLICATA
 * @summary Dettaglio modulo di un tipo domanda
 */
export const getFrontTipiDomandaIdModuliId = (
  idTipoDomanda: string,
  idModulo: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ModuloSezioneCompletoDTO>(
    {
      url: `/framework-cmp-domande/tipi-domanda/${idTipoDomanda}/moduli/${idModulo}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontTipiDomandaIdModuliIdQueryKey = (
  idTipoDomanda: string,
  idModulo: string,
) =>
  [
    `/framework-cmp-domande/tipi-domanda/${idTipoDomanda}/moduli/${idModulo}`,
  ] as const;

export const getGetFrontTipiDomandaIdModuliIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaIdModuliId>>,
  TError = ErrorType<unknown>,
>(
  idTipoDomanda: string,
  idModulo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaIdModuliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontTipiDomandaIdModuliId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontTipiDomandaIdModuliIdQueryKey(idTipoDomanda, idModulo);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontTipiDomandaIdModuliId>>
  > = ({ signal }) =>
    getFrontTipiDomandaIdModuliId(
      idTipoDomanda,
      idModulo,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(idTipoDomanda && idModulo),
    ...queryOptions,
  };
};

export type GetFrontTipiDomandaIdModuliIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontTipiDomandaIdModuliId>>
>;
export type GetFrontTipiDomandaIdModuliIdQueryError = ErrorType<unknown>;

/**
 * @summary Dettaglio modulo di un tipo domanda
 */
export const useGetFrontTipiDomandaIdModuliId = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaIdModuliId>>,
  TError = ErrorType<unknown>,
>(
  idTipoDomanda: string,
  idModulo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaIdModuliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontTipiDomandaIdModuliIdQueryOptions(
    idTipoDomanda,
    idModulo,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ritorna il dettaglio di un modulo di un tipo domanda PUBBLICATA
 * @summary Dettaglio modulo di un tipo domanda
 */
export const getFrontTipiDomandaIdModuliModuloPrincipale = (
  idTipoDomanda: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ModuloSezioneCompletoDTO>(
    {
      url: `/framework-cmp-domande/tipi-domanda/${idTipoDomanda}/moduli/modulo-principale`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontTipiDomandaIdModuliModuloPrincipaleQueryKey = (
  idTipoDomanda: string,
) =>
  [
    `/framework-cmp-domande/tipi-domanda/${idTipoDomanda}/moduli/modulo-principale`,
  ] as const;

export const getGetFrontTipiDomandaIdModuliModuloPrincipaleQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getFrontTipiDomandaIdModuliModuloPrincipale>
  >,
  TError = ErrorType<unknown>,
>(
  idTipoDomanda: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaIdModuliModuloPrincipale>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontTipiDomandaIdModuliModuloPrincipale>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontTipiDomandaIdModuliModuloPrincipaleQueryKey(idTipoDomanda);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontTipiDomandaIdModuliModuloPrincipale>>
  > = ({ signal }) =>
    getFrontTipiDomandaIdModuliModuloPrincipale(
      idTipoDomanda,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idTipoDomanda, ...queryOptions };
};

export type GetFrontTipiDomandaIdModuliModuloPrincipaleQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getFrontTipiDomandaIdModuliModuloPrincipale>>
  >;
export type GetFrontTipiDomandaIdModuliModuloPrincipaleQueryError =
  ErrorType<unknown>;

/**
 * @summary Dettaglio modulo di un tipo domanda
 */
export const useGetFrontTipiDomandaIdModuliModuloPrincipale = <
  TData = Awaited<
    ReturnType<typeof getFrontTipiDomandaIdModuliModuloPrincipale>
  >,
  TError = ErrorType<unknown>,
>(
  idTipoDomanda: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaIdModuliModuloPrincipale>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetFrontTipiDomandaIdModuliModuloPrincipaleQueryOptions(
      idTipoDomanda,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ritorna l'elenco dei beneficiari per l'utente intermediario corrente per tutte le intermediazioni associate al tipo di domanda
 * @summary Elenco Beneficiari di Intermediazione
 */
export const getFrontTipiDomandaIdIntermediazioniBeneficiari = (
  idTipoDomanda: string,
  params?: GetFrontTipiDomandaIdIntermediazioniBeneficiariParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<IntermediazioneDTO[]>(
    {
      url: `/framework-cmp-domande/tipi-domanda/${idTipoDomanda}/intermediazioni/beneficiari`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontTipiDomandaIdIntermediazioniBeneficiariQueryKey = (
  idTipoDomanda: string,
  params?: GetFrontTipiDomandaIdIntermediazioniBeneficiariParams,
) =>
  [
    `/framework-cmp-domande/tipi-domanda/${idTipoDomanda}/intermediazioni/beneficiari`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontTipiDomandaIdIntermediazioniBeneficiariQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getFrontTipiDomandaIdIntermediazioniBeneficiari>
  >,
  TError = ErrorType<unknown>,
>(
  idTipoDomanda: string,
  params?: GetFrontTipiDomandaIdIntermediazioniBeneficiariParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getFrontTipiDomandaIdIntermediazioniBeneficiari>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontTipiDomandaIdIntermediazioniBeneficiari>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontTipiDomandaIdIntermediazioniBeneficiariQueryKey(
      idTipoDomanda,
      params,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontTipiDomandaIdIntermediazioniBeneficiari>>
  > = ({ signal }) =>
    getFrontTipiDomandaIdIntermediazioniBeneficiari(
      idTipoDomanda,
      params,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idTipoDomanda, ...queryOptions };
};

export type GetFrontTipiDomandaIdIntermediazioniBeneficiariQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getFrontTipiDomandaIdIntermediazioniBeneficiari>>
  >;
export type GetFrontTipiDomandaIdIntermediazioniBeneficiariQueryError =
  ErrorType<unknown>;

/**
 * @summary Elenco Beneficiari di Intermediazione
 */
export const useGetFrontTipiDomandaIdIntermediazioniBeneficiari = <
  TData = Awaited<
    ReturnType<typeof getFrontTipiDomandaIdIntermediazioniBeneficiari>
  >,
  TError = ErrorType<unknown>,
>(
  idTipoDomanda: string,
  params?: GetFrontTipiDomandaIdIntermediazioniBeneficiariParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getFrontTipiDomandaIdIntermediazioniBeneficiari>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetFrontTipiDomandaIdIntermediazioniBeneficiariQueryOptions(
      idTipoDomanda,
      params,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ottiene i dettagli di un tipo domanda tramite codice.
 * @summary Recupera tipo domanda per codice
 */
export const getFrontTipiDomandaCodiceCode = (
  code: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoDomandaDTO>(
    {
      url: `/framework-cmp-domande/tipi-domanda/codice/${code}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontTipiDomandaCodiceCodeQueryKey = (code: string) =>
  [`/framework-cmp-domande/tipi-domanda/codice/${code}`] as const;

export const getGetFrontTipiDomandaCodiceCodeQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaCodiceCode>>,
  TError = ErrorType<WsProblema>,
>(
  code: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaCodiceCode>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontTipiDomandaCodiceCode>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontTipiDomandaCodiceCodeQueryKey(code);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontTipiDomandaCodiceCode>>
  > = ({ signal }) =>
    getFrontTipiDomandaCodiceCode(code, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!code, ...queryOptions };
};

export type GetFrontTipiDomandaCodiceCodeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontTipiDomandaCodiceCode>>
>;
export type GetFrontTipiDomandaCodiceCodeQueryError = ErrorType<WsProblema>;

/**
 * @summary Recupera tipo domanda per codice
 */
export const useGetFrontTipiDomandaCodiceCode = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaCodiceCode>>,
  TError = ErrorType<WsProblema>,
>(
  code: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaCodiceCode>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontTipiDomandaCodiceCodeQueryOptions(
    code,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
